// Shared, ref-counted live store for the CURRENT user's `User` read model
// (wiki/feature-frontend-cqrs-dataflow.md §2 — "Shared live store"; the
// documented `User` case spanning the app shell, /billing, /billing/success,
// /profile and /library).
//
// PROBLEM this solves. The signed-in user's `User` read model (subscription
// role, AI credits, storage, profile) is read by more than one surface at once —
// the app shell on every route, plus /billing, /billing/success, /profile and
// /library. If each opened its own read-channel pipeline, the
// `readModelsHub` would dedupe the `WatchOwner('User')` JOIN (one subscription)
// but NOT the refetch: each instance owns its own push handler, so a single
// `EntityChanged` would fan out into N whoami GETs. This store is therefore the
// app's ONLY `User` push handler — `currentUserLive.svelte.test.ts` enforces it.
//
// PATTERN. One shared `liveReadModel('User')` behind a module singleton: one
// `WatchOwner('User')`, one refetch per push, every consumer reactive off the
// same data — which IS the global `auth` store, since the refetch is
// `refresh()` (whoami). A page reads `$auth.user` as it always has; this store
// only owns the subscription + push-driven refetch that keeps `$auth` current
// without any polling. `UserProjection` is ASYNC, so the push is also the
// "projection caught up" signal every user-mutating command settles on (the name
// dialog, the profile form, a checkout claim) — a single post-command whoami
// races the Marten daemon and, on a loss, nothing ever re-reads it.
//
// HAND-ROLLED. Unlike the Progress-page `masteryProgressLive` RxJS pilot, this
// store is built on the hand-rolled `liveReadModel` helper (the app-wide
// standard) wrapped in a ref-counted lifecycle, exposing the same public surface
// (`acquire` / `release` / `.data` / `hydrated`). The refetch is `refresh()`
// (whoami → the `User` read model projection of the current user), so a `User`
// push converges the chrome, billing, and profile views on one GET.
//
// QUOTA. Quota (`DailyPracticeCounter`) is computed state, NOT a pushable read
// model on this surface, so it is refreshed best-effort ALONGSIDE the `User`
// push: each push runs `quotaStore.refresh()` too. This is what lets
// /billing/success settle on the `User` push (the subscription role lands
// on `User` after the Stripe webhook) and pick up the post-payment quota reset
// without a poll. It is a best-effort companion refresh, not a guarantee that
// quota is pushable.

import { get } from 'svelte/store';
import { auth, refresh, refreshAccessToken, type CurrentUser } from '$lib/stores/auth';
import { quotaStore } from '$lib/stores/quotaStore';
import { liveReadModel, type LiveReadModel, type IsAuthenticatedGate } from '$lib/api/liveReadModel.svelte';
import { readModelsHub, type ReadModelsHub } from '$lib/api/readModelsHub';

/** The model type string the backend pushes for the current user. */
const MODEL = 'User';

/** Construction seams — defaulted to the real collaborators, injectable for tests. */
export type CurrentUserLiveDeps = {
  hub?: ReadModelsHub;
  isAuthenticated?: IsAuthenticatedGate;
  /** The whoami refetch that updates the global `auth` store. Defaults to `refresh`. */
  refresh?: () => Promise<void>;
  /** Best-effort companion refresh fired alongside each `User` push. Defaults to `quotaStore.refresh`. */
  refreshQuota?: () => Promise<void>;
  /**
   * Re-mints the access token (POST /api/auth/refresh) on each `User` push — the
   * SignalR reactive accelerator. A pushed `EntityChanged{User}` means the user's
   * roles / subscription / active-state just changed server-side, so we collapse
   * the ≤15-minute cheap-surface staleness of the access-token claims to sub-second
   * by re-minting BEFORE the whoami refetch. Defaults to `refreshAccessToken`. See
   * wiki/feature-auth-token-lifecycle.md §10.
   */
  refreshAccessToken?: () => Promise<boolean>;
  /** Reads the current user off the `auth` store after a refresh. Defaults to `get(auth).user`. */
  readUser?: () => CurrentUser | null;
};

const defaultReadUser = (): CurrentUser | null => get(auth).user ?? null;

/**
 * Module-singleton, ref-counted live store over the current user's `User` read
 * model. The app shell (`routes/+layout.svelte`) acquires it for the signed-in
 * id, and /billing, /billing/success, /profile and /library acquire it
 * on mount and `release()` on unmount; the store starts ONE
 * `liveReadModel('User')` on the first acquire and stops it on the last release.
 * It is the ONLY `User` push handler in the app.
 */
class CurrentUserLiveStore {
  /** Mirrors the underlying helper's data — the current user, or null. */
  get data(): CurrentUser | null {
    return this.#live?.data ?? null;
  }

  /** Mirrors the underlying helper's hydrate flag. */
  get hydrated(): boolean {
    return this.#live?.hydrated ?? false;
  }

  /** The running helper, or null when no consumer is acquired. */
  #live: LiveReadModel<CurrentUser | null> | null = null;
  /** The userId the running helper is bound to (its WatchOwner is the caller's own id). */
  #userId: string | null = null;
  /**
   * When true, the running helper subscribes the read channel even for a GUEST —
   * the guest self-watch (wiki/feature-checkout-claim.md §C / AC-CC-5). A Guest
   * may `WatchOwner('User')` on its OWN UUID group (owner-safe — strictly its own
   * id), so an in-place claim push reaches it live. Raised by `acquireSelfWatch`
   * and STICKY for the life of the running helper — a plain `acquire` never
   * lowers it (see `#acquire`).
   */
  #forceWatch = false;
  /** Active consumers — start on 0→1, stop on 1→0. */
  #refCount = 0;

  readonly #hub: ReadModelsHub;
  readonly #isAuthenticated?: IsAuthenticatedGate;
  readonly #refresh: () => Promise<void>;
  readonly #refreshQuota: () => Promise<void>;
  readonly #refreshAccessToken: () => Promise<boolean>;
  readonly #readUser: () => CurrentUser | null;

  constructor(deps: CurrentUserLiveDeps = {}) {
    this.#hub = deps.hub ?? readModelsHub;
    this.#isAuthenticated = deps.isAuthenticated;
    this.#refresh = deps.refresh ?? refresh;
    this.#refreshQuota = deps.refreshQuota ?? (() => quotaStore.refresh());
    this.#refreshAccessToken = deps.refreshAccessToken ?? refreshAccessToken;
    this.#readUser = deps.readUser ?? defaultReadUser;
  }

  /**
   * Register a consumer for `userId`. The FIRST consumer builds + starts the
   * shared helper (hydrate via whoami → `WatchOwner('User')` → refetch-on-push);
   * a later consumer with the SAME userId reuses it (one subscription, one GET
   * per push). A consumer acquiring with a DIFFERENT userId restarts the helper
   * against the new id (a sign-out/in while a consumer stays mounted). Each
   * `acquire` pairs with exactly one `release`.
   */
  acquire(userId: string): void {
    this.#acquire(userId, false);
  }

  /**
   * Register a consumer that subscribes the read channel even when the caller is
   * a GUEST — the guest self-watch (wiki/feature-checkout-claim.md §C / AC-CC-5).
   * The checkout-claim success screen acquires this way so the in-place claim's
   * `EntityChanged{User}` push flips the screen to authenticated/granted live,
   * with no poll. `WatchOwner('User')` is scoped server-side to the caller's own
   * id, so a guest watching its own UUID group is owner-safe. Same ref-counted
   * lifecycle + one-subscription guarantee as `acquire`.
   */
  acquireSelfWatch(userId: string): void {
    this.#acquire(userId, true);
  }

  #acquire(userId: string, forceWatch: boolean): void {
    // An id change restarts the helper — the running subscription belongs to an
    // identity nobody is asking about any more.
    //
    // The force-watch mode only ever restarts UPWARDS (false → true), which is
    // what installs the forced gate a still-guest consumer needs. A DOWNGRADE
    // never restarts: `forceWatch` is strictly a superset (the gate is
    // `() => true` instead of the auth store), so an authenticated consumer
    // behaves identically under it, while tearing down would drop the watch a
    // guest mid-claim depends on — the app shell's `acquire` lands the instant
    // the claim flips the shell to authenticated, and a restart there would
    // rebuild the helper behind the auth gate that refuses to subscribe a guest,
    // losing the very push the success screen settles on.
    const upgradeWatch = forceWatch && !this.#forceWatch;
    if (this.#live && (this.#userId !== userId || upgradeWatch)) {
      this.#teardown();
    }

    this.#refCount += 1;

    if (!this.#live) {
      this.#userId = userId;
      this.#forceWatch = forceWatch;
      this.#start();
    }
  }

  /**
   * Release a previously-acquired consumer. When the LAST consumer releases, the
   * helper is stopped (unwatch + dispose). Safe to over-call: the ref-count
   * floors at 0.
   */
  release(): void {
    if (this.#refCount === 0) return;
    this.#refCount -= 1;
    if (this.#refCount === 0 && this.#live) {
      this.#teardown();
    }
  }

  #start(): void {
    // The auth store's boot `whoami` has usually already loaded the user, so the
    // helper publishes that instead of re-running the load — the app shell
    // acquires on every page load and must not cost a second whoami. When auth
    // has not settled (`null`), the helper hydrates for real.
    const known = this.#readUser();
    const live = liveReadModel<CurrentUser | null>({
      model: MODEL,
      ...(known ? { initialValue: known } : {}),
      // The refetch IS whoami: it updates the global `auth` store and returns the
      // resulting user, so a `User` push converges chrome + billing + profile on
      // ONE GET. Quota rides the SAME push best-effort (wiki §6) — a push means
      // the subscription/credits (and, post-payment, the reset quota) changed.
      load: async () => {
        // SignalR reactive accelerator: a `User` push means roles / subscription /
        // active-state changed, so re-mint the access token FIRST (current roles +
        // premium_until) before refetching whoami — collapsing the ≤15-minute
        // cheap-surface staleness to sub-second for this connected client. The
        // refresh is FE-only and best-effort: it rides existing infra (the backend
        // already pushes User changes) and a refused refresh simply leaves whoami
        // to resolve the new state. See wiki/feature-auth-token-lifecycle.md §10.
        await this.#refreshAccessToken().catch(() => false);
        await this.#refresh();
        await this.#refreshQuota().catch(() => {});
        return this.#readUser();
      },
      hub: this.#hub,
      // Guest self-watch forces the gate true so a GUEST subscribes its own
      // owner group (wiki §F / AC-CC-6); otherwise the injected/default gate
      // keeps the read channel authenticated-only (a guest hydrates but never
      // subscribes — wiki §3a).
      ...(this.#forceWatch
        ? { isAuthenticated: () => true }
        : this.#isAuthenticated
          ? { isAuthenticated: this.#isAuthenticated }
          : {}),
    });
    this.#live = live;
    void live.start();
  }

  #teardown(): void {
    this.#live?.stop();
    this.#live = null;
    this.#userId = null;
    this.#forceWatch = false;
  }
}

/** The process-wide shared current-user live store. */
export const currentUserLive = new CurrentUserLiveStore();

/** Test seam: build an isolated store over fake deps (the singleton binds the real collaborators). */
export function createCurrentUserLiveStore(deps: CurrentUserLiveDeps): CurrentUserLiveStore {
  return new CurrentUserLiveStore(deps);
}

export type { CurrentUserLiveStore };
